import unfetch from 'isomorphic-unfetch';

export interface Notification {
  id: string;
  text: string;
  status: number;
}

export interface BannerNotification extends Notification {
  removable: boolean;
}

export interface NotificationResponse {
  regularNotifications: Notification[];
  bannerNotifications: BannerNotification[];
  regularNotificationsTotal: number;
}

export interface NotificationStatusResponse {
  status: boolean;
  message: string;
}

export interface ReadNotificationStatusResponse {
  status: boolean;
  redirectUrl: string;
}

async function request <T>(url: string, options?: RequestInit): Promise<T> {
  const response = await unfetch(url, {
    cache: 'default',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
    },
    ...options,
  });

  if (!response.ok) {
    throw await response.json();
  }

  if (response.status === 204) {
    return;
  }

  const result = await response.json();

  // Notifications endpoints will fail with a 200 response that includes a json object
  if (result?.status === false) {
    throw new Error(result.message);
  }

  return result;
}

/**
 * Fetch all unread notifications.
 */
async function getAll(): Promise<NotificationResponse> {
  const path =
    typeof window === 'undefined'
      ? '/'
      : window.location.pathname.replace(/^\//, '');
  const encodedPath = encodeURIComponent(path);

  return request(`/api/notifications?path=${encodedPath}`, {
    method: 'GET',
  });
}

/**
 * Delete all of the current member's notifications.
 */
async function deleteAll(): Promise<void> {
  return request('/api/notification/delete-all', {
    method: 'POST',
  });
}

/**
 * Delete a specific notification for the current member.
 */
async function deleteOne(id: string): Promise<NotificationStatusResponse> {
  return request(`/api/notification/delete/${id}`, {
    method: 'POST',
  });
}

/**
 * Mark the specified notification as read for the current member.
 */
async function readOne(id: string, url: string): Promise<ReadNotificationStatusResponse> {
  return request(`/api/notification/read/${id}/${url}`, {
    method: 'GET',
  });
}

export default {
  getAll,
  deleteAll,
  deleteOne,
  readOne
};
